import PaginationControls from "@/components/tables/pagination/PaginationControls";
import PaginationInfo from "@/components/tables/pagination/PaginationInfo";
import TableComponent from "@/components/tables/table/TableComponent";
import { useLazyGetTransHistoryQuery } from "@/services/transactionAPI";
import {
  ActionIcon,
  Box,
  Center,
  Flex,
  Group,
  Loader,
  rem,
  Space,
  Text,
  TextInput,
} from "@mantine/core";
import { IconFilter, IconSearch } from "@tabler/icons-react";
import dayjs from "dayjs";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterStatementDrawer from "./FilterStatementDrawer";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { useNavigate } from "react-router-dom";
import RoutesString from "@/pages/routesString";

const ViewTrans = ({
  sourceLinkId,
  accountNo,
  title,
  description,
}: {
  sourceLinkId: string;
  accountNo: string;
  title?: string;
  description?: string;
}) => {
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure(false);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const [triggerTrans, { data: dataTrans, isLoading }] =
    useLazyGetTransHistoryQuery();
  const transData = get(dataTrans, "body.fundsTransfer.items", []);
  const totalPages = get(dataTrans, "body.fundsTransfer.totalPages", 1);
  // Bộ lọc ĐÃ áp dụng cho lần gọi API gần nhất. Phải lưu riêng vì drawer gọi
  // form.reset() khi đóng → không thể đọc form.values khi phân trang/lọc lại.
  const [appliedFilters, setAppliedFilters] = useState<{
    fromDate: string;
    toDate: string;
    minAmount: string;
    maxAmount: string;
    transType: string;
    amountType: string;
    transChannel: "" | "ONLINE" | "OFFLINE";
  }>({
    fromDate: dayjs().subtract(30, "day").format("YYYYMMDD"),
    toDate: dayjs().format("YYYYMMDD"),
    minAmount: "",
    maxAmount: "",
    transType: "",
    amountType: "",
    transChannel: "",
  });
  const form = useForm({
    initialValues: {
      fromDate: dayjs().subtract(30, "day").toDate() as Date | null,
      toDate: dayjs().toDate() as Date | null,
      maxAmount: "",
      minAmount: "",
      transType: "",
      amountType: "",
      transChannel: "",
      keyword: "",
    },
  });

  const valueKeyWord = useDebouncedValue(form.values.keyword, 500);
  // Gọi API mỗi khi đổi trang / keyword / bộ lọc đã áp dụng. Đọc từ appliedFilters
  // (không đọc form.values) để phân trang giữ nguyên điều kiện lọc.
  useEffect(() => {
    if (!sourceLinkId) return;
    triggerTrans({
      keyword: valueKeyWord[0] || "",
      sourceLinkId,
      fromDate: appliedFilters.fromDate,
      toDate: appliedFilters.toDate,
      minAmount: appliedFilters.minAmount,
      maxAmount: appliedFilters.maxAmount,
      transType: appliedFilters.transType,
      amountType: appliedFilters.amountType,
      pageNo: page,
      pageSize: 10,
      type: "HISTORY",
      transChannel: appliedFilters.transChannel,
    });
  }, [page, sourceLinkId, valueKeyWord[0], appliedFilters]);

  const handleSubmit = (values: any) => {
    form.setFieldValue("keyword", "");
    // Lưu snapshot bộ lọc; effect ở trên sẽ tự gọi API với trang = 1.
    setAppliedFilters({
      fromDate: values.fromDate
        ? dayjs(values.fromDate).format("YYYYMMDD")
        : "",
      toDate: values.toDate ? dayjs(values.toDate).format("YYYYMMDD") : "",
      minAmount: values.minAmount ? values.minAmount.toString() : "",
      maxAmount: values.maxAmount ? values.maxAmount.toString() : "",
      transType: values.transType || "",
      amountType: values.amountType || "",
      transChannel: (["", "ONLINE", "OFFLINE"].includes(values.transChannel)
        ? values.transChannel
        : "") as "" | "ONLINE" | "OFFLINE",
    });
    setPage(1);
    close();
  };
  return (
    <Box>
      {isLoading ? (
        <Center style={{ minHeight: 300 }}>
          <Loader size="lg" color="blue" />
        </Center>
      ) : (
        <>
          <Flex align="center" gap={8} py={16}>
            {/* Search input */}
            <TextInput
              placeholder={`${t("SEARCH")}`}
              icon={<IconSearch size={16} />}
              radius="sm"
              styles={{
                input: {
                  height: 40,
                  borderRadius: 10,
                },
              }}
              w={260}
              {...form.getInputProps("keyword")}
            />

            {/* Filter button */}
            <ActionIcon
              variant="outline"
              radius="md"
              size={39}
              color="blue"
              onClick={open}
              styles={{
                root: {
                  borderColor: "#2A81D0",
                },
              }}
            >
              <IconFilter size={18} />
            </ActionIcon>
          </Flex>
          <TableComponent
            dataSource={{
              columns: [
                { title: <Text ta="center">STT</Text> },
                { title: `${t("REFERENCE_CODE")}` },
                { title: `${t("TIME")}` },
                { title: `${t("CONTENT_BATCH")}`, size: 200 },
                { title: `${t("DEBIT_AMOUNT_COL")}` },
                { title: `${t("CREDIT_AMOUNT_COL")}` },
              ],
              data: transData
                // Lọc lại phía FE theo bộ lọc "Loại tiền" vì API có thể trả cả
                // ghi nợ lẫn ghi có: CREDIT chỉ giữ dòng có creditAmount = "+...",
                // DEBIT chỉ giữ dòng có debitAmount = "-..." (bỏ giá trị "-" rỗng).
                .filter((item: any) => {
                  const { amountType } = appliedFilters;
                  if (!amountType) return true;
                  const d = String(item.debitAmount ?? "").trim();
                  const c = String(item.creditAmount ?? "").trim();
                  if (amountType === "CREDIT") return c.startsWith("+");
                  if (amountType === "DEBIT")
                    return d.startsWith("-") && d !== "-";
                  return true;
                })
                .map((item: any, index: number) => {
                  // Hiển thị theo đúng dấu API trả: ghi nợ = "-" + số tiền,
                  // ghi có = "+" + số tiền. Giá trị "-" (rỗng) coi như không có.
                  const debitRaw = String(item.debitAmount ?? "").trim();
                  const creditRaw = String(item.creditAmount ?? "").trim();
                  const hasDebit =
                    debitRaw.startsWith("-") && debitRaw !== "-";
                  const hasCredit = creditRaw.startsWith("+");
                  return {
                  stt: (
                    <Text ta="center">
                      {(page - 1) * 10 + index + 1}
                    </Text>
                  ),
                  transactionId: (
                    <Text
                      weight={600}
                      c="#0171BB"
                      style={{
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 200,
                      }}
                      title={item.transId}
                      onClick={() =>
                        navigate(
                          `${RoutesString.TransHistoryDetail}/${item.transId}`,
                          {
                            state: {
                              transId: item.transId,
                              accountNo,
                              type: "HISTORY",
                            },
                          },
                        )
                      }
                    >
                      {item.transId}
                    </Text>
                  ),
                  transactionDate: (
                    <Text ta="left">
                      {item.displayTime} {item.displayDate}
                    </Text>
                  ),
                  transactionRemarks: <Text fw={600} ta="left">{item.fromMessage}</Text>,
                  debitAmount: (
                    <Text c={hasDebit ? "#F37021" : undefined} fw={600} ta="left">
                      {hasDebit ? `-${item.totalAmountDisplay}` : "-"}
                    </Text>
                  ),
                  creditAmount: (
                    <Text c={hasCredit ? "#34B529" : undefined} fw={600} ta="left">
                      {hasCredit ? `+${item.totalAmountDisplay}` : "-"}
                    </Text>
                  ),
                };
              }),
            }}
          />
        </>
      )}
      <Space h={rem(10)} />
      <Group position="apart" sx={{ padding: "12px 20px" }}>
        <PaginationInfo current={page} total={totalPages} />
        <PaginationControls
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(newPage) => setPage(newPage)}
        />
      </Group>
      <FilterStatementDrawer
        opened={opened}
        onClose={close}
        onSubmit={handleSubmit}
        form={form}
        title={title}
        description={description}
      />
    </Box>
  );
};

export default ViewTrans;
